import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  useCreateMedicalRecordsRequestMutation,
  useGetMyMedicalRecordsRequestsQuery,
  useSearchMedicalRecordsEncountersQuery,
  useRetryMedicalRecordsRequestMutation,
} from '../data';
import type { MedicalRecordsEncounterView, MedicalRecordsQueueItem } from '../data';
import type {
  CreateMedicalRecordsRequestResponse,
  MedicalRecordsEncounterResponse,
  MedicalRecordsQueueResponse,
  MedicalRecordsSort,
} from '../data/medicalRecordsViews';

const POLL_INTERVAL_MS = 5000;
const SLOW_POLL_INTERVAL_MS = 30000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
const ACTIVE_STATUSES = new Set(['Queued', 'InProgress']);

export type MedicalRecordsErrorKind = 'denied' | 'unavailable' | 'general';

/** Discriminated view-state for the encounter search (Constitution §84). */
export type MedicalRecordsSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; kind: MedicalRecordsErrorKind }
  | { status: 'empty' }
  | { status: 'data'; items: MedicalRecordsEncounterView[]; hasPrevious: boolean; hasNext: boolean };

/** Discriminated view-state for the current user's request queue (Constitution §84). */
export type MedicalRecordsQueueState =
  | { status: 'loading' }
  | { status: 'error'; kind: MedicalRecordsErrorKind }
  | { status: 'empty' }
  | {
      status: 'data';
      items: MedicalRecordsQueueItem[];
      isRefreshing: boolean;
      hasPrevious: boolean;
      hasNext: boolean;
    };

export type MedicalRecordsSubmissionState =
  | { status: 'idle' }
  | { status: 'accepted' }
  | { status: 'error'; kind: MedicalRecordsErrorKind | 'conflict' };

type SearchStateSource = {
  committedSearchTerm: string;
  isSearching: boolean;
  isSearchError: boolean;
  searchError: unknown;
  searchData?: MedicalRecordsEncounterResponse;
  pageCount: number;
};

type QueueStateSource = {
  isScoped: boolean;
  isQueueLoading: boolean;
  isQueueFetching: boolean;
  isQueueError: boolean;
  queueError: unknown;
  queueData?: MedicalRecordsQueueResponse;
  pageCount: number;
};

/** Maps a fetch error to a user-facing category: permission denial, service outage, or general failure. */
const classifyError = (error: unknown): MedicalRecordsErrorKind => {
  const status = (error as FetchBaseQueryError | undefined)?.status;
  if (status === 403) return 'denied';
  if (status === 503) return 'unavailable';
  return 'general';
};

const createSearchState = ({
  committedSearchTerm,
  isSearching,
  isSearchError,
  searchError,
  searchData,
  pageCount,
}: SearchStateSource): MedicalRecordsSearchState => {
  if (!committedSearchTerm) return { status: 'idle' };
  if (isSearching) return { status: 'loading' };
  if (isSearchError) return { status: 'error', kind: classifyError(searchError) };
  const items = searchData?.items ?? [];
  return items.length === 0
    ? { status: 'empty' }
    : {
        status: 'data',
        items,
        hasPrevious: pageCount > 1,
        hasNext: Boolean(searchData?.continuationToken),
      };
};

const createQueueState = ({
  isScoped,
  isQueueLoading,
  isQueueFetching,
  isQueueError,
  queueError,
  queueData,
  pageCount,
}: QueueStateSource): MedicalRecordsQueueState => {
  const isPending = [!isScoped, isQueueLoading, isQueueFetching && !queueData].some(Boolean);
  if (isPending) return { status: 'loading' };
  if (isQueueError) return { status: 'error', kind: classifyError(queueError) };
  const items = queueData?.items ?? [];
  return items.length === 0
    ? { status: 'empty' }
    : {
        status: 'data',
        items,
        isRefreshing: isQueueFetching,
        hasPrevious: pageCount > 1,
        hasNext: Boolean(queueData?.continuationToken),
      };
};

const getActiveRequestedEncounterIds = (queueState: MedicalRecordsQueueState): ReadonlySet<string> =>
  new Set(queueState.status === 'data'
    ? queueState.items.filter((item) => ACTIVE_STATUSES.has(item.status) || item.blocksNewRequest === true).map((item) => item.encounterId)
    : []);

/** Falls back to a non-crypto value only if `crypto.randomUUID` is unavailable. */
const generateIdempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

type MedicalRecordsScope = {
  company: string;
  centerId: string;
  scopeKey: string;
  isScoped: boolean;
};

const createMedicalRecordsScope = (company: string, centerId: string): MedicalRecordsScope => ({
  company,
  centerId,
  scopeKey: `${company}:${centerId}`,
  isScoped: Boolean(company) && Boolean(centerId),
});

const shouldPollQueue = (queue: MedicalRecordsQueueResponse, pageCount: number): boolean =>
  [
    queue.items.some((item) => ACTIVE_STATUSES.has(item.status)),
    Boolean(queue.continuationToken),
    pageCount > 1,
  ].some(Boolean);

const getRemainingPollDuration = (startedAt: number): number =>
  MAX_POLL_DURATION_MS - (Date.now() - startedAt);

type IdempotencySubmission = { key: string; requestId?: string };
type PollingDeadline = { startedAt: number | null; expired: boolean };
type CreateRequestTrigger = ReturnType<typeof useCreateMedicalRecordsRequestMutation>[0];

const INACTIVE_POLLING_DEADLINE: PollingDeadline = { startedAt: null, expired: false };

const retireCompletedSubmissions = (
  items: MedicalRecordsQueueItem[],
  submissions: Map<string, IdempotencySubmission>,
): void => {
  items.forEach((item) => {
    if (!ACTIVE_STATUSES.has(item.status)
      && submissions.get(item.encounterId)?.requestId === item.requestId) {
      submissions.delete(item.encounterId);
    }
  });
};

type SubmissionSource = {
  encounter: MedicalRecordsEncounterView;
  submittedScope: string;
  activeScope: MutableRefObject<string>;
  submissions: Map<string, IdempotencySubmission>;
  createRequest: CreateRequestTrigger;
  startPolling: () => void;
  setSubmissionState: Dispatch<SetStateAction<MedicalRecordsSubmissionState>>;
  onAccepted: () => void;
};

const submitDocuments = async (
  source: SubmissionSource,
  replaceTerminalReplay: boolean,
): Promise<void> => {
  const submission = source.submissions.get(source.encounter.encounterId)
    ?? { key: generateIdempotencyKey() };
  source.submissions.set(source.encounter.encounterId, submission);

  try {
    const response: CreateMedicalRecordsRequestResponse = await source.createRequest({
      encounterId: source.encounter.encounterId,
      company: source.encounter.company,
      centerId: source.encounter.centerId,
      idempotencyKey: submission.key,
    }).unwrap();
    if (source.activeScope.current !== source.submittedScope) return;

    if (!ACTIVE_STATUSES.has(response.status)) {
      source.submissions.delete(source.encounter.encounterId);
      if (replaceTerminalReplay) {
        await submitDocuments(source, false);
        return;
      }
    } else {
      submission.requestId = response.requestId;
      source.startPolling();
    }
    source.setSubmissionState({ status: 'accepted' });
    source.onAccepted();
  } catch (error) {
    if (source.activeScope.current !== source.submittedScope) return;
    source.setSubmissionState({ status: 'error', kind: classifySubmissionError(error) });
  }
};

const classifySubmissionError = (error: unknown): MedicalRecordsErrorKind | 'conflict' =>
  (error as FetchBaseQueryError | undefined)?.status === 409 ? 'conflict' : classifyError(error);

type QueueSource = {
  sort?: MedicalRecordsSort;
  company: string;
  centerId: string;
  isScoped: boolean;
  scopeKey: string;
  submissions: MutableRefObject<Map<string, IdempotencySubmission>>;
};

const getEffectiveQueuePaging = (
  source: {
    scopeChanged: boolean;
    pageTokens: (string | null)[];
    pollingInterval: number;
  },
): { pageTokens: (string | null)[]; continuationToken: string | null; pollingInterval: number } => {
  if (source.scopeChanged) {
    return { pageTokens: [null], continuationToken: null, pollingInterval: 0 };
  }

  return {
    pageTokens: source.pageTokens,
    continuationToken: source.pageTokens[source.pageTokens.length - 1],
    pollingInterval: source.pollingInterval,
  };
};

const usePollingDeadline = (): {
  pollingInterval: number;
  setDeadline: Dispatch<SetStateAction<PollingDeadline>>;
} => {
  const [deadline, setDeadline] = useState<PollingDeadline>(INACTIVE_POLLING_DEADLINE);

  useEffect(() => {
    if (deadline.startedAt === null) return;
    const remaining = getRemainingPollDuration(deadline.startedAt);
    if (remaining <= 0) {
      setDeadline({ startedAt: null, expired: true });
      return;
    }

    const timeout = window.setTimeout(
      () => setDeadline({ startedAt: null, expired: true }),
      remaining,
    );
    return () => window.clearTimeout(timeout);
  }, [deadline.startedAt]);

  const initialInterval = deadline.startedAt === null ? 0 : POLL_INTERVAL_MS;
  return {
    pollingInterval: deadline.expired ? SLOW_POLL_INTERVAL_MS : initialInterval,
    setDeadline,
  };
};

const useMedicalRecordsQueueState = ({
  sort,
  company,
  centerId,
  isScoped,
  scopeKey,
  submissions,
}: QueueSource): {
  queueState: MedicalRecordsQueueState;
  startPolling: () => void;
  showPreviousPage: () => void;
  showNextPage: () => void;
} => {
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const { pollingInterval, setDeadline } = usePollingDeadline();
  const pagingKey = `${scopeKey}:${JSON.stringify(sort)}`;
  const renderedScope = useRef(pagingKey);
  const scopeChanged = renderedScope.current !== pagingKey;
  const effectivePaging = getEffectiveQueuePaging({ scopeChanged, pageTokens, pollingInterval });
  renderedScope.current = pagingKey;
  const {
    currentData: queueData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetMyMedicalRecordsRequestsQuery(
    { company, centerId, continuationToken: effectivePaging.continuationToken, ...(sort ? { sort } : {}) },
    { skip: !isScoped, pollingInterval: effectivePaging.pollingInterval },
  );

  useEffect(() => {
    setPageTokens([null]);
    setDeadline(INACTIVE_POLLING_DEADLINE);
  }, [pagingKey, setDeadline]);

  useEffect(() => {
    if (!queueData) return;
    retireCompletedSubmissions(queueData.items, submissions.current);
    if (!shouldPollQueue(queueData, pageTokens.length)) {
      setDeadline(INACTIVE_POLLING_DEADLINE);
      return;
    }

    setDeadline((current) => current.expired || current.startedAt !== null
      ? current
      : { startedAt: Date.now(), expired: false });
  }, [queueData, pageTokens.length, submissions, setDeadline]);

  const queueState = useMemo(() => createQueueState({
    isScoped,
    isQueueLoading: isLoading,
    isQueueFetching: isFetching,
    isQueueError: isError,
    queueError: error,
    queueData,
    pageCount: effectivePaging.pageTokens.length,
  }), [isScoped, isLoading, isFetching, isError, error, queueData, effectivePaging.pageTokens.length]);

  const showPreviousPage = (): void => setPageTokens((tokens) => tokens.slice(0, -1));
  const showNextPage = (): void => {
    if (queueData?.continuationToken) {
      setPageTokens((tokens) => [...tokens, queueData.continuationToken]);
    }
  };

  return {
    queueState,
    startPolling: () => setDeadline({ startedAt: Date.now(), expired: false }),
    showPreviousPage,
    showNextPage,
  };
};

const useIdempotencySubmissions = (
  scopeKey: string,
): MutableRefObject<Map<string, IdempotencySubmission>> => {
  const submissions = useRef(new Map<string, IdempotencySubmission>());
  useEffect(() => submissions.current.clear(), [scopeKey]);
  return submissions;
};

const getEffectiveSearchPaging = (source: {
  scopeChanged: boolean;
  sortChanged: boolean;
  committedSearchTerm: string;
  pageTokens: (string | null)[];
}) => ({
  searchTerm: source.scopeChanged ? '' : source.committedSearchTerm,
  pageTokens: source.scopeChanged || source.sortChanged ? [null] : source.pageTokens,
});

const useMedicalRecordsSearchResults = (
  scope: MedicalRecordsScope,
  paging: ReturnType<typeof getEffectiveSearchPaging>,
  sort?: MedicalRecordsSort,
) => {
  const { searchTerm, pageTokens } = paging;
  const continuationToken = pageTokens[pageTokens.length - 1];
  const { currentData: searchData, isFetching, isError, error, refetch } = useSearchMedicalRecordsEncountersQuery(
    { company: scope.company, centerId: scope.centerId, searchTerm, continuationToken, ...(sort ? { sort } : {}) },
    { skip: !scope.isScoped || !searchTerm },
  );
  const searchState = useMemo(() => createSearchState({
    committedSearchTerm: searchTerm,
    isSearching: isFetching,
    isSearchError: isError,
    searchError: error,
    searchData,
    pageCount: pageTokens.length,
  }), [searchTerm, isFetching, isError, error, searchData, pageTokens.length]);
  return { searchState, nextToken: searchData?.continuationToken, refreshSearch: refetch };
};

const useMedicalRecordsSearch = (
  scope: MedicalRecordsScope,
  sort?: MedicalRecordsSort,
): {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  submitSearch: () => void;
  clearSearch: () => void;
  canClearSearch: boolean;
  searchState: MedicalRecordsSearchState;
  showPreviousPage: () => void;
  showNextPage: () => void;
} => {
  const [searchTerm, setSearchTerm] = useState('');
  const [committedSearchTerm, setCommittedSearchTerm] = useState('');
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const renderedScope = useRef(scope.scopeKey);
  const scopeChanged = renderedScope.current !== scope.scopeKey;
  const renderedSort = useRef(sort);
  const sortChanged = renderedSort.current !== sort;
  renderedSort.current = sort;
  const paging = getEffectiveSearchPaging({
    scopeChanged, sortChanged, committedSearchTerm, pageTokens,
  });
  const { searchTerm: effectiveSearchTerm, pageTokens: effectivePageTokens } = paging;
  renderedScope.current = scope.scopeKey;
  const { searchState, nextToken, refreshSearch } = useMedicalRecordsSearchResults(scope, paging, sort);

  useEffect(() => {
    setSearchTerm('');
    setCommittedSearchTerm('');
    setPageTokens([null]);
  }, [scope.scopeKey]);

  useEffect(() => setPageTokens([null]), [sort]);

  const submitSearch = (): void => {
    const repeatFirstPage = searchTerm.trim() === effectiveSearchTerm && effectivePageTokens.length === 1;
    setPageTokens([null]);
    setCommittedSearchTerm(searchTerm.trim());
    if (repeatFirstPage && searchState.status === 'error') refreshSearch();
  };
  const clearSearch = (): void => {
    setSearchTerm('');
    setCommittedSearchTerm('');
    setPageTokens([null]);
  };
  const showPreviousPage = (): void => setPageTokens((tokens) => tokens.slice(0, -1));
  const showNextPage = (): void => {
    if (nextToken) {
      setPageTokens((tokens) => [...tokens, nextToken]);
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    submitSearch,
    clearSearch,
    canClearSearch: Boolean(searchTerm || effectiveSearchTerm || effectivePageTokens.length > 1),
    searchState,
    showPreviousPage,
    showNextPage,
  };
};

const useMedicalRecordsSubmission = (
  scopeKey: string,
  submissions: MutableRefObject<Map<string, IdempotencySubmission>>,
  startPolling: () => void,
  onAccepted: () => void,
): {
  submissionState: MedicalRecordsSubmissionState;
  requestDocuments: (encounter: MedicalRecordsEncounterView) => Promise<void>;
  clearSubmissionFeedback: () => void;
  isRequesting: boolean;
} => {
  const activeScope = useRef(scopeKey);
  const [submissionState, setSubmissionState] = useState<MedicalRecordsSubmissionState>({ status: 'idle' });
  const [createRequest, { isLoading: isRequesting }] = useCreateMedicalRecordsRequestMutation();
  activeScope.current = scopeKey;

  useEffect(() => setSubmissionState({ status: 'idle' }), [scopeKey]);

  const requestDocuments = async (encounter: MedicalRecordsEncounterView): Promise<void> => {
    setSubmissionState({ status: 'idle' });
    await submitDocuments({
      encounter,
      submittedScope: scopeKey,
      activeScope,
      submissions: submissions.current,
      createRequest,
      startPolling,
      setSubmissionState,
      onAccepted,
    }, true);
  };

  return {
    submissionState,
    requestDocuments,
    clearSubmissionFeedback: () => setSubmissionState({ status: 'idle' }),
    isRequesting,
  };
};

/**
 * Medical Records feature hook: submit-driven encounter search, a Request Documents action per
 * result (stable Idempotency-Key per encounter retry), and the current user's request queue for this
 * company/center. Queue polling is bounded — it only runs while at least one request is
 * Queued/InProgress, and stops once every request reaches a terminal status.
 */
export function useMedicalRecordsRequest(company: string, centerId: string, sort?: MedicalRecordsSort): {
  clearSubmissionFeedback: () => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  submitSearch: () => void;
  clearSearch: () => void;
  canClearSearch: boolean;
  searchState: MedicalRecordsSearchState;
  submissionState: MedicalRecordsSubmissionState;
  requestDocuments: (encounter: MedicalRecordsEncounterView) => Promise<void>;
  showPreviousSearchPage: () => void;
  showNextSearchPage: () => void;
  isRequesting: boolean;
  activeRequestedEncounterIds: ReadonlySet<string>;
} {
  const scope = createMedicalRecordsScope(company, centerId);
  const submissions = useIdempotencySubmissions(scope.scopeKey);

  const {
    queueState,
    startPolling,
    showPreviousPage: showPreviousQueuePage,
    showNextPage: showNextQueuePage,
  } = useMedicalRecordsQueueState({
    company,
    centerId,
    isScoped: scope.isScoped,
    scopeKey: scope.scopeKey,
    submissions,
  });
  const {
    searchTerm,
    setSearchTerm,
    submitSearch,
    clearSearch,
    canClearSearch,
    searchState,
    showPreviousPage: showPreviousSearchPage,
    showNextPage: showNextSearchPage,
  } = useMedicalRecordsSearch(scope, sort);
  const { submissionState, requestDocuments, clearSubmissionFeedback, isRequesting } = useMedicalRecordsSubmission(
    scope.scopeKey,
    submissions,
    startPolling,
    clearSearch,
  );

  return {
    clearSubmissionFeedback,
    searchTerm,
    setSearchTerm,
    submitSearch,
    clearSearch: () => {
      clearSearch();
      clearSubmissionFeedback();
    },
    canClearSearch: canClearSearch || submissionState.status !== 'idle',
    searchState,
    submissionState,
    requestDocuments,
    showPreviousSearchPage,
    showNextSearchPage,
    isRequesting,
    activeRequestedEncounterIds: getActiveRequestedEncounterIds(queueState),
  };
}

export function useMedicalRecordsQueue(company: string, centerId: string, sort?: MedicalRecordsSort): {
  queueState: MedicalRecordsQueueState;
  showPreviousPage: () => void;
  showNextPage: () => void;
} {
  const scope = createMedicalRecordsScope(company, centerId);
  const submissions = useIdempotencySubmissions(scope.scopeKey);
  const { queueState, showPreviousPage, showNextPage } = useMedicalRecordsQueueState({
    sort,
    company,
    centerId,
    isScoped: scope.isScoped,
    scopeKey: scope.scopeKey,
    submissions,
  });

  return { queueState, showPreviousPage, showNextPage };
}

export function useMedicalRecordsRetry(item: MedicalRecordsQueueItem) {
  const [retry, { isLoading }] = useRetryMedicalRecordsRequestMutation();
  const [feedback, setFeedback] = useState<'accepted' | 'error' | null>(null);
  const pending = useRef(false);
  useEffect(() => setFeedback(null), [item.activeAttemptId, item.status]);
  const submit = async () => {
    if (pending.current) return;
    pending.current = true;
    setFeedback(null);
    try {
      await retry({ requestId: item.requestId, company: item.company, centerId: item.centerId,
        expectedAttemptId: item.activeAttemptId ?? null }).unwrap();
      setFeedback('accepted');
    } catch {
      setFeedback('error');
    } finally {
      pending.current = false;
    }
  };
  return { submit, feedback, isLoading };
}
